import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core';
import { computePosition, flip, offset, shift, size, autoUpdate } from '@floating-ui/dom';
import { debounceEvent } from '@utils/utils';
import { isSpecTest, messageId, assignDescription } from '../../utils/form';
import { danger, enlarge } from '@pine-ds/icons/icons';
import type {
  MultiselectOption,
  MultiselectChangeEventDetail,
  MultiselectSearchEventDetail,
  MultiselectLoadOptionsEventDetail,
  MultiselectCreateEventDetail,
  AsyncResponse,
  CreateResponse,
} from './multiselect-interface';

/**
 * @slot (default) - Static option elements for the multiselect
 * @slot empty - Custom empty state message when no options match
 * @slot loading - Custom loading indicator
 * @part trigger - The trigger surface that opens the dropdown panel
 * @part pill-toggle - The chevron button inside the inline pill trigger
 */
@Component({
  tag: 'pds-multiselect',
  styleUrl: 'pds-multiselect.scss',
  shadow: true,
  formAssociated: true,
})
export class PdsMultiselect {
  private triggerEl?: HTMLButtonElement | HTMLDivElement;
  private pillInlineTriggerEl?: HTMLDivElement;
  private searchInputEl?: HTMLInputElement;
  private containerEl?: HTMLElement;
  private listboxEl?: HTMLElement;
  private panelEl?: HTMLElement;
  private internals?: ElementInternals;
  private abortController?: AbortController;
  private fetchDebounceTimer?: number;
  private fetchTimeoutTimer?: number;
  private observer?: MutationObserver;
  private cleanupAutoUpdate?: () => void;

  @Element() el!: HTMLPdsMultiselectElement;

  /**
   * A unique identifier used for the underlying component `id` attribute.
   */
  @Prop() componentId!: string;

  /**
   * Text to be displayed as the multiselect label.
   */
  @Prop() label?: string;

  /**
   * Placeholder text for the input field.
   */
  @Prop() placeholder?: string = 'Select...';

  /**
   * Placeholder text for the search input inside the dropdown panel.
   * @default 'Find...'
   */
  @Prop() searchPlaceholder: string = 'Find...';

  /**
   * Whether to close the panel after an option is selected.
   * Defaults to `false` (panel stays open for multi-select).
   */
  @Prop() closePanelOnSelect: boolean = false;

  /**
   * Specifies the name. Submitted with the form as part of a name/value pair.
   */
  @Prop() name?: string;

  /**
   * Array of selected option values.
   */
  @Prop({ mutable: true }) value: string[] = [];

  /**
   * Determines whether or not the multiselect is disabled.
   */
  @Prop() disabled: boolean = false;

  /**
   * URL endpoint for async data fetching.
   */
  @Prop() asyncUrl?: string;

  /**
   * HTTP method for async requests.
   */
  @Prop() asyncMethod: 'GET' | 'POST' = 'GET';

  /**
   * Debounce delay in milliseconds for search/fetch.
   */
  @Prop() debounce: number = 300;

  /**
   * Timeout in milliseconds for async fetch requests.
   */
  @Prop() fetchTimeout: number = 30000;

  /**
   * Maximum number of selections allowed.
   */
  @Prop() maxSelections?: number;

  /**
   * Maximum height of the dropdown before scrolling.
   */
  @Prop() maxHeight: string = '300px';

  /**
   * Width of the trigger button (and reference for dropdown positioning).
   */
  @Prop() triggerWidth: string = '100%';

  /**
   * Minimum width of the dropdown panel.
   */
  @Prop() minWidth: string = '250px';

  /**
   * Width of the dropdown panel. Defaults to the trigger width.
   */
  @Prop() panelWidth?: string;

  /**
   * Visually hides the label but keeps it accessible.
   */
  @Prop() hideLabel: boolean = false;

  /**
   * Hides the selected items summary section in the dropdown panel.
   */
  @Prop() hideSelectedItems: boolean = false;

  /**
   * Display mode for selected items outside the dropdown panel.
   * `'count'` shows "N item(s)" text in the trigger (default). `'pill'` renders
   * selected items as dismissible pds-chip tags.
   * @default 'count'
   */
  @Prop() selectedDisplay: 'count' | 'pill' = 'count';

  /**
   * Position of pill chips when `selectedDisplay` is `'pill'`.
   * `'inline'` places chips inside the trigger; `'below'` places chips in a
   * flex-wrap row directly below the trigger.
   * @default 'inline'
   */
  @Prop() pillPosition: 'inline' | 'below' = 'inline';

  /**
   * Maximum chips shown inline before collapsing to a "+N more" badge.
   * Only applies when `selectedDisplay='pill'` and `pillPosition='inline'`.
   * @default 3
   */
  @Prop() maxInlinePills: number = 3;

  /**
   * Error message to display.
   */
  @Prop() errorMessage?: string;

  /**
   * Helper message to display below the input.
   */
  @Prop() helperMessage?: string;

  /**
   * If true, the multiselect is in an invalid state.
   */
  @Prop() invalid?: boolean;

  /**
   * If true, the multiselect is required.
   */
  @Prop() required: boolean = false;

  /**
   * Whether the component is currently loading async options.
   */
  @Prop({ mutable: true }) loading: boolean = false;

  /**
   * Options provided externally (for consumer-managed async).
   * When using `group` on options, keep each group block contiguous in the array.
   * The same `group` label appearing again after other items produces a separate header (same as native `<optgroup>`).
   */
  @Prop() options?: MultiselectOption[];

  /**
   * Function to format async results. Receives raw API response item.
   */
  @Prop() formatResult?: (item: unknown) => MultiselectOption;

  /**
   * URL endpoint for creating new options. When set, shows "Add" option when no matches found.
   */
  @Prop() createUrl?: string;

  /**
   * CSRF token for authenticated requests. If not provided, attempts to read from meta tag.
   */
  @Prop() csrfToken?: string;

  /**
   * CSRF header name for authenticated requests. Defaults to `X-CSRF-Token`.
   */
  @Prop() csrfHeaderName?: string = 'X-CSRF-Token';

  // Internal state
  @State() isOpen: boolean = false;
  @State() searchQuery: string = '';
  @State() highlightedIndex: number = -1;
  @State() internalOptions: MultiselectOption[] = [];
  @State() selectedItems: MultiselectOption[] = [];
  @State() currentPage: number = 1;
  @State() hasMore: boolean = false;
  @State() creating: boolean = false;
  @State() private removalAnnouncement: string = '';

  // Flag to prevent focusout from closing during open transition
  private isOpening: boolean = false;
  // Flag to suppress dismiss event when panel closes due to selection (not user dismissal)
  private isClosingViaSelection: boolean = false;
  // Flag to track if initial async fetch has been triggered (prevents double fetch)
  private initialAsyncFetchTriggered: boolean = false;
  // Flag to track if value changed during loading and needs resolution after fetch completes
  private pendingUnresolvedFetch: boolean = false;

  /**
   * Emitted when selection changes.
   */
  @Event() pdsMultiselectChange!: EventEmitter<MultiselectChangeEventDetail>;

  /**
   * Emitted on search input (for consumer-managed async).
   */
  @Event() pdsMultiselectSearch!: EventEmitter<MultiselectSearchEventDetail>;

  /**
   * Emitted to request more options (pagination).
   */
  @Event() pdsMultiselectLoadOptions!: EventEmitter<MultiselectLoadOptionsEventDetail>;

  /**
   * Emitted when a new option is created.
   */
  @Event() pdsMultiselectCreate!: EventEmitter<MultiselectCreateEventDetail>;

  /**
   * Emitted when the dropdown is dismissed via Escape key or click outside.
   *
   * This event fires only when the user explicitly dismisses the panel without making a selection:
   * - ✅ Fires: Pressing Escape key while dropdown is open
   * - ✅ Fires: Clicking outside the component while dropdown is open
   * - ❌ Does NOT fire: When panel closes due to selection (including when `closePanelOnSelect` is true)
   * - ❌ Does NOT fire: When panel closes programmatically via `closeDropdown()`
   *
   * Equivalent to Sage's `onEscapeHook`. Use this to restore parent UI state or run cleanup when the user cancels their interaction.
   */
  @Event() pdsMultiselectDismiss!: EventEmitter<void>;

  private originalSearchEmitter?: EventEmitter<MultiselectSearchEventDetail>;

  connectedCallback() {
    // Initialize ElementInternals for form association (only once per element instance)
    if (this.el.attachInternals && !this.internals) {
      this.internals = this.el.attachInternals();
    }
  }

  componentWillLoad() {
    this.originalSearchEmitter = this.pdsMultiselectSearch;
    this.syncSelectedItems();
  }

  componentDidLoad() {
    this.setupDebounce();
    this.setupMutationObserver();
    this.setupSlotChangeListener();
    this.updateFormValue();

    // Ensure preselected values sync after DOM is fully ready
    // This handles cases where slot content loads after initial render (e.g., in docs/MDX)
    requestAnimationFrame(() => {
      this.updateOptionsFromSlot();
      this.syncSelectedItems();
    });

    // If we have preselected values and asyncUrl, fetch options to resolve them
    // This ensures the trigger shows "X items" instead of placeholder on initial render
    if (this.asyncUrl && this.ensureValueArray().length > 0) {
      this.initialAsyncFetchTriggered = true;
      this.fetchOptions('', 1);
    }
  }

  private setupSlotChangeListener() {
    const slot = this.el.shadowRoot?.querySelector('slot:not([name])') as HTMLSlotElement;
    if (slot) {
      slot.addEventListener('slotchange', () => {
        this.updateOptionsFromSlot();
        this.syncSelectedItems();
      });
      // Also call it immediately in case content is already slotted
      this.updateOptionsFromSlot();
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.cleanupAutoUpdate?.();
    this.clearAsyncFetchState();
  }

  @Watch('debounce')
  protected setupDebounce() {
    const { pdsMultiselectSearch, debounce, originalSearchEmitter } = this;
    this.pdsMultiselectSearch = debounce === undefined
      ? originalSearchEmitter ?? pdsMultiselectSearch
      : debounceEvent(pdsMultiselectSearch, debounce);
  }

  @Watch('value')
  protected valueChanged(newValue: string[] | string) {
    // Handle JSON string values (from HTML attributes)
    if (typeof newValue === 'string') {
      try {
        const parsed = JSON.parse(newValue);
        if (Array.isArray(parsed)) {
          this.value = parsed;
          return; // The assignment will trigger this watcher again with the array
        }
      } catch {
        // Not valid JSON, treat as single value
        this.value = newValue ? [newValue] : [];
        return;
      }
    }
    this.syncSelectedItems();
    this.updateFormValue();

    // If using asyncUrl and some values couldn't be resolved, fetch options
    // This handles programmatic value changes where the options aren't loaded yet
    if (this.asyncUrl) {
      const valueArray = this.ensureValueArray();
      const hasUnresolvedValues = valueArray.length > 0 && this.selectedItems.length < valueArray.length;
      if (hasUnresolvedValues) {
        if (this.loading) {
          // Mark that we need to re-check after current fetch completes
          this.pendingUnresolvedFetch = true;
        } else {
          this.fetchOptions('', 1);
        }
      }
    }
  }

  @Watch('options')
  protected optionsChanged() {
    if (this.options) {
      this.internalOptions = [...this.options];
    }
  }

  @Watch('internalOptions')
  protected internalOptionsChanged() {
    // Re-sync selected items when options become available
    // This handles the case where value is set before options are loaded (e.g., from slot)
    this.syncSelectedItems();
  }

  /**
   * Sets focus on the trigger button.
   */
  @Method()
  async setFocus() {
    this.triggerEl?.focus();
  }

  /**
   * Clears all selected values and resets the component.
   */
  @Method()
  async clear() {
    const hadValues = this.value.length > 0 || this.searchQuery !== '';
    this.value = [];
    this.searchQuery = '';
    this.syncSelectedItems();
    this.updateFormValue();
    if (hadValues) {
      this.pdsMultiselectChange.emit({ values: [], items: [] });
    }
  }

  /**
   * Handle global keyboard events for accessibility.
   * Closes dropdown on Escape key press regardless of focus location.
   */
  @Listen('keydown', { target: 'window' })
  handleWindowKeyDown(event: KeyboardEvent) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.pdsMultiselectDismiss.emit();
      this.closeDropdown();
      this.triggerEl?.focus();
    }
  }

  private setupMutationObserver() {
    this.observer = new MutationObserver(() => {
      this.updateOptionsFromSlot();
    });

    this.observer.observe(this.el, {
      childList: true,
      subtree: true,
    });
  }

  private updateOptionsFromSlot() {
    const slot = this.el.shadowRoot?.querySelector('slot:not([name])') as HTMLSlotElement;
    if (!slot) return;

    const options: MultiselectOption[] = [];

    slot.assignedElements({ flatten: true }).forEach(el => {
      if (el.tagName === 'OPTGROUP') {
        const optgroup = el as HTMLOptGroupElement;
        const groupLabel = optgroup.label;
        const groupDisabled = optgroup.disabled;
        optgroup.querySelectorAll('option').forEach((opt: HTMLOptionElement) => {
          const option: MultiselectOption = {
            id: opt.value,
            text: opt.textContent?.trim() || opt.value,
            disabled: opt.disabled || groupDisabled || undefined,
          };
          if (groupLabel) {
            option.group = groupLabel;
          }
          options.push(option);
        });
      } else if (el.tagName === 'OPTION') {
        const opt = el as HTMLOptionElement;
        options.push({
          id: opt.value,
          text: opt.textContent?.trim() || opt.value,
          disabled: opt.disabled || undefined,
        });
      }
    });

    // Only update if we actually found options AND we're not using async/external options
    // Don't clear internalOptions if slot returns empty (might be mid-DOM-update)
    if (options.length > 0 && !this.asyncUrl && !this.options) {
      this.internalOptions = options;
    }
  }

  private clearAsyncFetchState() {
    if (this.fetchDebounceTimer !== undefined) {
      window.clearTimeout(this.fetchDebounceTimer);
      this.fetchDebounceTimer = undefined;
    }
    if (this.fetchTimeoutTimer !== undefined) {
      window.clearTimeout(this.fetchTimeoutTimer);
      this.fetchTimeoutTimer = undefined;
    }
    this.abortController?.abort();
    this.abortController = undefined;
  }

  private debouncedFetchAsyncOptions(query: string, page: number = 1) {
    if (!this.asyncUrl) return;

    if (this.fetchDebounceTimer !== undefined) {
      window.clearTimeout(this.fetchDebounceTimer);
    }

    const delay = Math.max(0, this.debounce ?? 0);
    this.fetchDebounceTimer = window.setTimeout(() => {
      this.fetchDebounceTimer = undefined;
      this.fetchOptions(query, page);
    }, delay);
  }

  private syncSelectedItems() {
    // Ensure value is an array (may be string from HTML attribute)
    const valueArray = this.ensureValueArray();
    const allOptions = this.getAllOptions();

    // Map values to options, preserving existing selectedItems for values not yet in options
    const newSelectedItems: MultiselectOption[] = [];
    const existingItemsMap = new Map(this.selectedItems.map(item => [String(item.id), item]));

    valueArray.forEach(val => {
      // First try to find in available options
      const option = allOptions.find(opt => String(opt.id) === String(val));

      if (option) {
        newSelectedItems.push(option);
      } else if (existingItemsMap.has(String(val))) {
        // If not in options but exists in current selectedItems, preserve it
        // This handles the case where async data hasn't loaded yet or newly created items
        newSelectedItems.push(existingItemsMap.get(String(val))!);
      }
      // Note: We don't create placeholders for values without matching options.
      // This ensures selectedItems remains empty until options are actually loaded,
      // which matches the expected behavior for preselected values.
    });

    this.selectedItems = newSelectedItems;
  }

  private ensureValueArray(): string[] {
    // Handle JSON string values passed via HTML attribute
    if (typeof this.value === 'string') {
      try {
        const parsed = JSON.parse(this.value as unknown as string);
        if (Array.isArray(parsed)) {
          this.value = parsed;
          return parsed;
        }
      } catch {
        // Not valid JSON, treat as single value
        const singleValue = this.value as unknown as string;
        this.value = singleValue ? [singleValue] : [];
        return this.value;
      }
    }
    return Array.isArray(this.value) ? this.value : [];
  }

  private getAllOptions(): MultiselectOption[] {
    return this.options || this.internalOptions;
  }


  private getFilteredOptions(): MultiselectOption[] {
    const allOptions = this.getAllOptions();
    const query = this.searchQuery.toLowerCase();

    const filtered = allOptions.filter(opt => {
      // Filter by search query only - don't filter out selected items
      if (query) {
        return opt.text.toLowerCase().includes(query);
      }
      return true;
    });

    // Add create option if enabled and no matches found
    if (this.createUrl && this.searchQuery.trim() && filtered.length === 0) {
      return [{
        id: '__create__',
        text: this.searchQuery.trim(),
        isCreateOption: true,
      }];
    }

    return filtered;
  }

  private updateFormValue() {
    if (isSpecTest()) return;

    if (this.internals?.setFormValue) {
      // Ensure value is an array before iterating
      const valueArray = this.ensureValueArray();

      // Submit as multiple values with same name (native select multiple behavior)
      const formData = new FormData();
      valueArray.forEach(val => {
        if (this.name) {
          formData.append(this.name, val);
        }
      });
      this.internals.setFormValue(formData);

      // Update validity state for required validation
      if (this.required && valueArray.length === 0) {
        this.internals.setValidity(
          { valueMissing: true },
          'Please select at least one option.',
          this.triggerEl
        );
      } else {
        this.internals.setValidity({});
      }
    }
  }

  private getCsrfToken(): string | null {
    // Use provided token if available
    if (this.csrfToken) {
      return this.csrfToken;
    }

    // Try to read from meta tag
    const metaTag = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement;
    return metaTag?.content || null;
  }

  private async fetchOptions(query: string, page: number = 1) {
    if (!this.asyncUrl) return;

    // Abort any pending request and clear existing timeout
    this.abortController?.abort();
    if (this.fetchTimeoutTimer !== undefined) {
      window.clearTimeout(this.fetchTimeoutTimer);
      this.fetchTimeoutTimer = undefined;
    }

    this.abortController = new AbortController();
    this.loading = true;

    // Set up timeout for fetch request
    this.fetchTimeoutTimer = window.setTimeout(() => {
      this.abortController?.abort();
      this.fetchTimeoutTimer = undefined;
    }, this.fetchTimeout);

    try {
      // Guard against malformed asyncUrl
      let url: URL;
      try {
        url = new URL(this.asyncUrl, window.location.origin);
      } catch (urlError) {
        throw new TypeError(`Invalid asyncUrl: ${this.asyncUrl}`);
      }

      if (this.asyncMethod === 'GET') {
        url.searchParams.set('search', query);
        url.searchParams.set('page', String(page));
      }

      const csrfToken = this.getCsrfToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };

      if (csrfToken) {
        const headerName = this.csrfHeaderName || 'X-CSRF-Token';
        headers[headerName] = csrfToken;
      }

      const response = await fetch(url.toString(), {
        method: this.asyncMethod,
        signal: this.abortController.signal,
        headers,
        ...(this.asyncMethod === 'POST' && {
          body: JSON.stringify({ search: query, page }),
        }),
      });

      // Clear timeout on successful response
      if (this.fetchTimeoutTimer !== undefined) {
        window.clearTimeout(this.fetchTimeoutTimer);
        this.fetchTimeoutTimer = undefined;
      }

      if (!response.ok) throw new Error('Failed to fetch options');

      const data: AsyncResponse = await response.json();

      const formattedResults = data.results.map(item => {
        // If formatResult is provided, use it
        if (this.formatResult) {
          return this.formatResult(item);
        }

        // Defensive check: ensure item is an object before spreading
        if (typeof item === 'object' && item !== null) {
          return {
            id: item.id,
            text: item.text,
            ...item,
          } as MultiselectOption;
        }

        // Handle primitives by converting to safe shape
        return {
          id: String(item),
          text: String(item),
        } as MultiselectOption;
      });

      if (page === 1) {
        this.internalOptions = formattedResults;
      } else {
        this.internalOptions = [...this.internalOptions, ...formattedResults];
      }

      this.hasMore = data.totalCount ? this.internalOptions.length < data.totalCount : false;
      this.currentPage = page;

    } catch (error) {
      // Clear timeout on error
      if (this.fetchTimeoutTimer !== undefined) {
        window.clearTimeout(this.fetchTimeoutTimer);
        this.fetchTimeoutTimer = undefined;
      }

      if ((error as Error).name !== 'AbortError') {
        console.error('PdsMultiselect: Failed to fetch options', error);
      }
    } finally {
      this.loading = false;

      // Check if value changed during loading and still has unresolved values
      if (this.pendingUnresolvedFetch) {
        this.pendingUnresolvedFetch = false;
        const valueArray = this.ensureValueArray();
        const hasUnresolvedValues = valueArray.length > 0 && this.selectedItems.length < valueArray.length;
        if (hasUnresolvedValues) {
          this.fetchOptions('', 1);
        }
      }
    }
  }

  private async createOption(query: string) {
    if (!this.createUrl || !query.trim()) return;

    // Reentrancy guard: prevent duplicate POSTs if creation is already in-flight
    if (this.creating) return;

    this.creating = true;

    try {
      const url = new URL(this.createUrl, window.location.origin);

      const csrfToken = this.getCsrfToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };

      if (csrfToken) {
        const headerName = this.csrfHeaderName || 'X-CSRF-Token';
        headers[headerName] = csrfToken;
      }

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers,
        body: JSON.stringify({ text: query.trim() }),
      });

      if (!response.ok) throw new Error('Failed to create option');

      const data: CreateResponse = await response.json();

      const newOption: MultiselectOption = {
        id: data.id,
        text: data.text,
        ...data,
      };

      // Add to internal options first
      this.internalOptions = [...this.internalOptions, newOption];

      // Select the new option
      this.value = [...this.value, String(newOption.id)];

      // Sync selected items to update display with new option
      this.syncSelectedItems();

      // Emit create event
      this.pdsMultiselectCreate.emit({
        query: query.trim(),
        newOption,
      });

      // Emit change event with synced items
      this.pdsMultiselectChange.emit({
        values: this.value,
        items: this.selectedItems,
      });

      // Clear search and keep dropdown open
      this.searchQuery = '';
      this.highlightedIndex = -1;

      // Focus back on search input
      requestAnimationFrame(() => {
        this.searchInputEl?.focus();
      });

    } catch (error) {
      console.error('PdsMultiselect: Failed to create option', error);
    } finally {
      this.creating = false;
    }
  }

  private handleTriggerClick = (e?: MouseEvent) => {
    if (this.disabled) return;
    // In inline-pill mode, ignore clicks that originated inside a pds-chip.
    // Chip close-button clicks are handled by handlePillRemove; chip body clicks should not toggle the panel.
    if (e && this.selectedDisplay === 'pill' && this.pillPosition === 'inline') {
      const fromChip = e.composedPath().some(
        el => (el as { tagName?: string }).tagName?.toLowerCase() === 'pds-chip'
      );
      if (fromChip) return;
    }

    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  };

  private handleTriggerKeyDown = (e: KeyboardEvent) => {
    // Ignore keydown events that bubbled from child elements (e.g. chip close buttons).
    // Only handle keys when the trigger itself is the event target.
    if (e.target !== this.triggerEl) return;
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!this.isOpen) {
          this.openDropdown();
        }
        break;
    }
  };

  private handleSearchInputChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    this.searchQuery = target.value;
    this.highlightedIndex = -1;

    // Emit search event for consumer-managed async
    this.pdsMultiselectSearch.emit({ query: this.searchQuery });

    // Fetch from async URL if configured
    if (this.asyncUrl) {
      this.debouncedFetchAsyncOptions(this.searchQuery, 1);
    }
  };

  private handleSearchInputKeyDown = (e: KeyboardEvent) => {
    const filteredOptions = this.getFilteredOptions();

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        let nextIndex = this.highlightedIndex + 1;
        while (nextIndex < filteredOptions.length && filteredOptions[nextIndex]?.disabled) {
          nextIndex++;
        }
        if (nextIndex < filteredOptions.length) {
          this.highlightedIndex = nextIndex;
          this.scrollOptionIntoView();
        }
        break;
      }

      case 'ArrowUp': {
        e.preventDefault();
        // Clamp to 0 so ArrowUp from uninitialised state (-1) attempts the first option;
        // stays at -1 if that option is disabled.
        let prevIndex = Math.max(this.highlightedIndex - 1, 0);
        while (prevIndex > 0 && filteredOptions[prevIndex]?.disabled) {
          prevIndex--;
        }
        if (!filteredOptions[prevIndex]?.disabled) {
          this.highlightedIndex = prevIndex;
          this.scrollOptionIntoView();
        }
        break;
      }

      case 'Enter':
        e.preventDefault();
        if (this.highlightedIndex >= 0) {
          const option = filteredOptions[this.highlightedIndex];
          if (option) {
            this.selectOption(option);
          }
        }
        break;

      // Escape is handled by the global @Listen('keydown') handler

      case 'Tab':
        this.closeDropdown();
        break;
    }
  };

  private handleContainerFocusOut = () => {
    // Use setTimeout to delay the check - this allows click events and focus transitions to complete
    // before we decide to close the dropdown
    setTimeout(() => {
      // Don't close if we're in the middle of opening, already closed,
      // or closing due to a selection (not a user dismissal)
      if (!this.isOpen || this.isOpening || this.isClosingViaSelection) return;

      const activeElement = document.activeElement;

      // Check if focus is within our component's shadow root
      const isInShadowRoot = this.el.shadowRoot?.contains(activeElement);

      // Also check if focus is on the host element itself
      const isOnHost = activeElement === this.el;

      if (!isInShadowRoot && !isOnHost) {
        this.pdsMultiselectDismiss.emit();
        this.closeDropdown();
      }
    }, 0);
  };

  private openDropdown() {
    if (this.disabled) return;

    this.isOpening = true;
    this.isOpen = true;
    this.highlightedIndex = -1;

    // Trigger initial fetch if async (skip if already fetching from componentDidLoad)
    if (this.asyncUrl && this.internalOptions.length === 0 && !this.initialAsyncFetchTriggered) {
      this.debouncedFetchAsyncOptions(this.searchQuery, 1);
    }
    // Reset the flag so subsequent opens can fetch if needed
    this.initialAsyncFetchTriggered = false;

    requestAnimationFrame(() => {
      this.positionDropdown();
      // Focus the search input after the panel is positioned
      this.searchInputEl?.focus();
      // Clear the opening flag after focus has moved
      setTimeout(() => {
        this.isOpening = false;
      }, 50);
    });
  }

  private closeDropdown() {
    this.isOpen = false;
    this.highlightedIndex = -1;
    this.searchQuery = '';

    // Reset the selection-close guard after the focusout handler's setTimeout has resolved
    if (this.isClosingViaSelection) {
      setTimeout(() => {
        this.isClosingViaSelection = false;
      }, 0);
    }

    // Clean up auto-update
    if (this.cleanupAutoUpdate) {
      this.cleanupAutoUpdate();
      this.cleanupAutoUpdate = undefined;
    }
  }

  private positionDropdown() {
    if (!this.containerEl || !this.panelEl) return;
    // In inline pill mode, triggerEl is the small chevron button — use the full-width
    // wrapper div as the Floating UI reference so the panel aligns with the whole trigger.
    const referenceEl = this.pillInlineTriggerEl || this.triggerEl || this.containerEl;

    const { minWidth, panelWidth } = this;

    const updatePosition = () => {
      computePosition(referenceEl!, this.panelEl!, {
        placement: 'bottom-start',
        strategy: 'absolute',
        middleware: [
          offset(12),
          flip(),
          shift({ padding: 8 }),
          size({
            apply: ({ rects, elements }) => {
              Object.assign(elements.floating.style, {
                width: panelWidth ?? `${rects.reference.width}px`,
                minWidth,
              });
            },
          }),
        ],
      }).then(({ x, y }) => {
        if (this.panelEl) {
          this.panelEl.style.left = `${x}px`;
          this.panelEl.style.top = `${y}px`;
        }
      });
    };

    // Initial position
    updatePosition();

    // Set up auto-update for window resize and scroll
    const cleanupAutoUpdate = autoUpdate(
      referenceEl!,
      this.panelEl,
      updatePosition
    );
    this.cleanupAutoUpdate = () => {
      cleanupAutoUpdate();
      this.clearAsyncFetchState();
    };
  }

  private scrollOptionIntoView() {
    requestAnimationFrame(() => {
      const highlighted = this.listboxEl?.querySelector(`[data-index="${this.highlightedIndex}"]`) as HTMLElement;
      highlighted?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }

  private toggleOption(option: MultiselectOption) {
    if (option.disabled) return;

    // Handle create option
    if (option.isCreateOption) {
      // Prevent multiple create calls while one is in-flight
      if (!this.creating) {
        this.createOption(this.searchQuery);
      }
      return;
    }

    const optionId = String(option.id);
    const isSelected = this.value.includes(optionId);

    if (isSelected) {
      // Remove from selection
      this.value = this.value.filter(v => v !== optionId);
    } else {
      // Add to selection
      if (this.maxSelections && this.value.length >= this.maxSelections) {
        return;
      }

      this.value = [...this.value, optionId];
    }

    // Sync selected items to ensure no duplicates and accurate state
    this.syncSelectedItems();

    // Emit change event with synced items
    this.pdsMultiselectChange.emit({
      values: this.value,
      items: this.selectedItems,
    });

    if (this.closePanelOnSelect) {
      this.isClosingViaSelection = true;
      this.closeDropdown();
      this.triggerEl?.focus();
    } else {
      // Keep focus on search input, don't close dropdown
      this.searchInputEl?.focus();
    }
  }

  private selectOption(option: MultiselectOption) {
    // For keyboard navigation - toggle the option
    this.toggleOption(option);
  }

  private handleOptionMouseDown = (option: MultiselectOption) => (e: MouseEvent) => {
    e.preventDefault(); // Prevent focus change
    this.toggleOption(option);
  };

  private handleOptionMouseEnter = (index: number, option: MultiselectOption) => () => {
    if (option.disabled) return;
    this.highlightedIndex = index;
  };


  private handleScroll = (e: Event) => {
    if (!this.asyncUrl || !this.hasMore || this.loading) return;

    const target = e.target as HTMLElement;
    const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;

    // Load more when near bottom (within 50px)
    if (scrollBottom < 50) {
      this.pdsMultiselectLoadOptions.emit({
        query: this.searchQuery,
        page: this.currentPage + 1,
      });
      this.debouncedFetchAsyncOptions(this.searchQuery, this.currentPage + 1);
    }
  };


  private getGroupedRenderItems(filteredOptions: MultiselectOption[]): Array<
    | { type: 'group'; group: string; options: Array<{ option: MultiselectOption; index: number }> }
    | { type: 'option'; option: MultiselectOption; index: number }
  > {
    type GroupItem = { type: 'group'; group: string; options: Array<{ option: MultiselectOption; index: number }> };
    type OptionItem = { type: 'option'; option: MultiselectOption; index: number };
    const items: Array<GroupItem | OptionItem> = [];
    let currentGroup: GroupItem | null = null;

    filteredOptions.forEach((option, index) => {
      const group = option.group;
      if (group !== undefined) {
        if (!currentGroup || currentGroup.group !== group) {
          currentGroup = { type: 'group', group, options: [] };
          items.push(currentGroup);
        }
        currentGroup.options.push({ option, index });
      } else {
        currentGroup = null;
        items.push({ type: 'option', option, index });
      }
    });

    return items;
  }

  private renderOption(option: MultiselectOption, index: number, valueArray: string[]) {
    const isSelected = valueArray.includes(String(option.id));
    const isCreateOption = option.isCreateOption;
    const isDisabled = option.disabled;
    const isHighlighted = index === this.highlightedIndex && !isCreateOption;
    const optionId = `${this.componentId}-option-${index}`;
    const isCreateDisabled = isCreateOption && this.creating;

    return (
      <li
        key={String(option.id)}
        id={optionId}
        class={{
          'pds-multiselect__option': true,
          'pds-multiselect__option--highlighted': isHighlighted,
          'pds-multiselect__option--selected': isSelected,
          'pds-multiselect__option--create': isCreateOption,
          'pds-multiselect__option--disabled': isDisabled || isCreateDisabled,
        }}
        role="option"
        aria-selected={isSelected ? 'true' : 'false'}
        aria-disabled={isDisabled || isCreateDisabled ? 'true' : undefined}
        aria-label={isCreateOption ? `Create new tag: ${option.text}` : undefined}
        data-index={index}
        onMouseDown={this.handleOptionMouseDown(option)}
        onMouseEnter={this.handleOptionMouseEnter(index, option)}
      >
        {isCreateOption ? (
          <pds-box class="pds-multiselect__create-option" align-items="center" gap="xs">
            <pds-icon name="add" size="small" />
            <pds-text>Add "{option.text}"</pds-text>
          </pds-box>
        ) : (
          <pds-checkbox
            componentId={`${this.componentId}-checkbox-${index}`}
            checked={isSelected}
            label={option.text}
            disabled={isDisabled}
            style={{ pointerEvents: 'none' }}
          />
        )}
      </li>
    );
  }

  private renderSelectedItemsList() {
    if (this.hideSelectedItems || this.selectedItems.length === 0) return null;

    return (
      <div class="pds-multiselect__selected-section">
        <ul class="pds-multiselect__selected-list" role="list">
          {this.selectedItems.map(item => (
            <li key={String(item.id)} class="pds-multiselect__selected-item">
              {item.text}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  private renderDropdown() {
    if (!this.isOpen) return null;

    const filteredOptions = this.getFilteredOptions();
    const valueArray = this.ensureValueArray();
    const hasSlottedEmpty = !!this.el.querySelector('[slot="empty"]');
    const hasSlottedLoading = !!this.el.querySelector('[slot="loading"]');

    return (
      <div
        class="pds-multiselect__panel"
        ref={el => (this.panelEl = el)}
        style={{ minWidth: this.minWidth }}
      >
        {/* Search input */}
        <div class="pds-multiselect__search">
          <pds-icon name="search" size="small" />
          <input
            ref={el => (this.searchInputEl = el)}
            type="text"
            class="pds-multiselect__search-input"
            placeholder={this.searchPlaceholder}
            value={this.searchQuery}
            aria-label="Search options"
            aria-controls={`${this.componentId}-listbox`}
            aria-activedescendant={this.highlightedIndex >= 0 ? `${this.componentId}-option-${this.highlightedIndex}` : undefined}
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded="true"
            aria-autocomplete="list"
            autocomplete="off"
            onInput={this.handleSearchInputChange}
            onKeyDown={this.handleSearchInputKeyDown}
          />
        </div>

        {/* Selected items section */}
        {this.renderSelectedItemsList()}

        {/* Options list */}
        <ul
          class="pds-multiselect__listbox"
          role="listbox"
          aria-multiselectable="true"
          aria-label={this.label || 'Options'}
          id={`${this.componentId}-listbox`}
          ref={el => (this.listboxEl = el)}
          style={{ maxHeight: this.maxHeight }}
          onScroll={this.handleScroll}
        >
          {this.loading && (
            <li class="pds-multiselect__loading" role="presentation">
              {hasSlottedLoading ? (
                <slot name="loading" />
              ) : (
                <pds-loader size="small" />
              )}
            </li>
          )}

          {!this.loading && filteredOptions.length === 0 && (
            <li class="pds-multiselect__empty" role="presentation">
              {hasSlottedEmpty ? (
                <slot name="empty" />
              ) : (
                <span>No options found</span>
              )}
            </li>
          )}

          {this.getGroupedRenderItems(filteredOptions).map((item, itemIndex) => {
            if (item.type === 'group') {
              return (
                <li
                  key={`group-${itemIndex}`}
                  role="group"
                  aria-label={item.group}
                  class="pds-multiselect__group"
                >
                  <span class="pds-multiselect__group-header" aria-hidden="true">
                    {item.group}
                  </span>
                  <ul class="pds-multiselect__group-list" role="presentation">
                    {item.options.map(({ option, index }) => this.renderOption(option, index, valueArray))}
                  </ul>
                </li>
              );
            }

            return this.renderOption(item.option, item.index, valueArray);
          })}

          {this.hasMore && !this.loading && (
            <li class="pds-multiselect__load-more" role="presentation">
              <pds-loader size="small" />
            </li>
          )}
        </ul>
      </div>
    );
  }

  private handlePillRemove = (item: MultiselectOption) => () => {
    if (this.disabled) return;
    const optionId = String(item.id);
    this.value = this.value.filter(v => v !== optionId);
    this.syncSelectedItems();
    this.pdsMultiselectChange.emit({ values: this.value, items: this.selectedItems });
    // Clear first so screen readers re-announce even when the same item is removed twice
    this.removalAnnouncement = '';
    queueMicrotask(() => { this.removalAnnouncement = `${item.text} removed`; });
    this.isClosingViaSelection = true;
    if (this.isOpen) {
      this.searchInputEl?.focus();
    } else {
      this.triggerEl?.focus();
    }
    setTimeout(() => { this.isClosingViaSelection = false; }, 0);
  };

  private renderInlinePills() {
    const hasSelections = this.selectedItems.length > 0;
    if (!hasSelections) {
      return (
        <span class="pds-multiselect__trigger-text pds-multiselect__trigger-text--placeholder">
          {this.placeholder || 'Select...'}
        </span>
      );
    }
    const variant = this.disabled ? 'text' : 'tag';
    const visibleItems = this.selectedItems.slice(0, Math.max(1, this.maxInlinePills));
    const overflowCount = this.selectedItems.length - visibleItems.length;
    return (
      <div
        class="pds-multiselect__pill-list pds-multiselect__pill-list--inline"
        aria-label="Selected items"
      >
        {visibleItems.map(item => (
          <pds-chip
            key={String(item.id)}
            component-id={`${this.componentId}-pill-${item.id}`}
            variant={variant}
            size="sm"
            sentiment="neutral"
            onPdsTagCloseClick={this.handlePillRemove(item)}
          >{item.text}</pds-chip>
        ))}
        {overflowCount > 0 && (
          <span class="pds-multiselect__pill-overflow">+{overflowCount}</span>
        )}
      </div>
    );
  }

  private renderBelowPills() {
    if (this.selectedItems.length === 0) return null;
    const variant = this.disabled ? 'text' : 'tag';
    return (
      <div
        class="pds-multiselect__pill-list pds-multiselect__pill-list--below"
        aria-label="Selected items"
      >
        {this.selectedItems.map(item => (
          <pds-chip
            key={String(item.id)}
            component-id={`${this.componentId}-pill-${item.id}`}
            variant={variant}
            size="md"
            sentiment="neutral"
            onPdsTagCloseClick={this.handlePillRemove(item)}
          >{item.text}</pds-chip>
        ))}
      </div>
    );
  }

  private getTriggerText(): string {
    const count = this.selectedItems.length;
    if (count === 0 || (this.selectedDisplay === 'pill' && this.pillPosition === 'below')) {
      return this.placeholder || 'Select...';
    }
    return `${count} item${count === 1 ? '' : 's'}`;
  }

  render() {
    const hasSelections = this.selectedItems.length > 0;

    return (
      <Host
        aria-disabled={this.disabled ? 'true' : null}
      >
        <div class="pds-multiselect" onFocusout={this.handleContainerFocusOut}>
          {this.label && (
            <label
              id={`${this.componentId}-label`}
              htmlFor={this.componentId}
              class={{
                'pds-multiselect__label': true,
                'visually-hidden': this.hideLabel,
              }}
            >
              {this.label}
            </label>
          )}

          <div
            class="pds-multiselect__wrapper"
            ref={el => (this.containerEl = el)}
            style={{ width: this.triggerWidth }}
          >
            {this.selectedDisplay === 'pill' && this.pillPosition === 'inline' ? (
              <div
                ref={el => (this.pillInlineTriggerEl = el || undefined)}
                part="trigger"
                class={{
                  'pds-multiselect__trigger': true,
                  'pds-multiselect__trigger--open': this.isOpen,
                  'pds-multiselect__trigger--disabled': this.disabled,
                  'pds-multiselect__trigger--invalid': this.invalid || !!this.errorMessage,
                  'pds-multiselect__trigger--has-value': hasSelections,
                  'pds-multiselect__trigger--pill-inline': true,
                }}
                onClick={this.handleTriggerClick}
              >
                {this.renderInlinePills()}
                <button
                  ref={el => (this.triggerEl = el)}
                  type="button"
                  id={this.componentId}
                  part="pill-toggle"
                  class="pds-multiselect__pill-toggle"
                  disabled={this.disabled}
                  aria-expanded={this.isOpen ? 'true' : 'false'}
                  aria-haspopup="listbox"
                  aria-labelledby={this.label ? `${this.componentId}-label` : undefined}
                  aria-required={this.required ? 'true' : undefined}
                  aria-describedby={assignDescription(
                    this.componentId,
                    this.invalid || !!this.errorMessage,
                    this.errorMessage || this.helperMessage
                  )}
                  aria-invalid={this.invalid || !!this.errorMessage ? 'true' : undefined}
                  onKeyDown={this.handleTriggerKeyDown}
                >
                  <pds-icon class="pds-multiselect__icon" icon={enlarge} />
                </button>
              </div>
            ) : (
              <button
                ref={el => (this.triggerEl = el)}
                type="button"
                part="trigger"
                class={{
                  'pds-multiselect__trigger': true,
                  'pds-multiselect__trigger--open': this.isOpen,
                  'pds-multiselect__trigger--disabled': this.disabled,
                  'pds-multiselect__trigger--invalid': this.invalid || !!this.errorMessage,
                  'pds-multiselect__trigger--has-value': hasSelections,
                }}
                id={this.componentId}
                disabled={this.disabled}
                aria-required={this.required ? 'true' : undefined}
                aria-expanded={this.isOpen ? 'true' : 'false'}
                aria-haspopup="listbox"
                aria-describedby={assignDescription(
                  this.componentId,
                  this.invalid || !!this.errorMessage,
                  this.errorMessage || this.helperMessage
                )}
                aria-invalid={this.invalid || !!this.errorMessage ? 'true' : undefined}
                onClick={this.handleTriggerClick}
                onKeyDown={this.handleTriggerKeyDown}
              >
                <span class={{
                  'pds-multiselect__trigger-text': true,
                  'pds-multiselect__trigger-text--placeholder': !hasSelections || (this.selectedDisplay === 'pill' && this.pillPosition === 'below'),
                }}>
                  {this.getTriggerText()}
                </span>
                <pds-icon class="pds-multiselect__icon" icon={enlarge} />
              </button>
            )}

            {this.renderDropdown()}
          </div>

          {this.selectedDisplay === 'pill' && this.pillPosition === 'below' && this.renderBelowPills()}

          <span
            class="visually-hidden"
            aria-live="polite"
            aria-atomic="true"
          >
            {this.removalAnnouncement}
          </span>

          {this.helperMessage && !(this.errorMessage && this.errorMessage.length > 0) && (
            <p class="pds-multiselect__helper" id={messageId(this.componentId, 'helper')}>
              {this.helperMessage}
            </p>
          )}

          {this.errorMessage && (
            <p class="pds-multiselect__error" id={messageId(this.componentId, 'error')}>
              <pds-icon icon={danger} size="small" />
              {this.errorMessage}
            </p>
          )}

          {/* Hidden slot for static options */}
          <div style={{ display: 'none' }}>
            <slot />
          </div>
        </div>
      </Host>
    );
  }
}
